import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { storeClubAuthAPI, memberAPI } from '../services/storeClubAPI';

interface MemberAnimal {
  id?: number;
  animal_type: string;
  animal_type_display?: string;
  name?: string;
  breed?: string;
  count?: number;
  weight_lbs?: number | null;
  activity_level?: string;
  life_stage?: string;
}

interface StoreClubMember {
  id: number;
  name: string;
  email: string;
  phone?: string;
  street_address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  distance_miles?: number | null;
  drive_time_minutes?: number | null;
  subscribe_to_reminders?: boolean;
  subscribe_to_weather_alerts?: boolean;
  preferred_reminder_days_before?: number;
  animals?: MemberAnimal[];
  member_code?: string;
}

interface StoreClubAuthContextType {
  member: StoreClubMember | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (data: any) => Promise<{ access: string; refresh: string; member: StoreClubMember }>;
  logout: () => void;
  refreshProfile: () => Promise<void>;
  setMemberFromTokens: (access: string, refresh: string, member: StoreClubMember) => void;
}

const StoreClubAuthContext = createContext<StoreClubAuthContextType | undefined>(undefined);

export const StoreClubAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [member, setMember] = useState<StoreClubMember | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadMemberFromStorage = useCallback(async () => {
    // Only check store-club auth on store-club routes (NOT bridge-club)
    const path = window.location.pathname;
    if (!path.startsWith('/store-club')) {
      setIsLoading(false);
      return;
    }
    // Check auth by calling /api/store-club/auth/me/ (cookies sent automatically)
    try {
      const profile = await storeClubAuthAPI.me();
      setMember(profile);
    } catch (error) {
      setMember(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMemberFromStorage();
  }, [loadMemberFromStorage]);

  const login = async (email: string, password: string) => {
    // Login endpoint sets httpOnly cookies automatically
    const response = await storeClubAuthAPI.login(email, password);
    const { member: memberData } = response;
    setMember(memberData);
  };

  const register = async (data: any) => {
    const response = await storeClubAuthAPI.register(data);
    return response;
  };

  const setMemberFromTokens = (access: string, refresh: string, memberData: StoreClubMember) => {
    // Cookies are set automatically by backend, just update state
    setMember(memberData);
  };

  const logout = async () => {
    await storeClubAuthAPI.logout();
    setMember(null);
  };

  const refreshProfile = async () => {
    const profile = await memberAPI.getProfile();
    setMember(profile);
  };

  return (
    <StoreClubAuthContext.Provider
      value={{
        member,
        isAuthenticated: !!member,
        isLoading,
        login,
        register,
        logout,
        refreshProfile,
        setMemberFromTokens,
      }}
    >
      {children}
    </StoreClubAuthContext.Provider>
  );
};

export const useStoreClubAuth = () => {
  const context = useContext(StoreClubAuthContext);
  if (!context) {
    throw new Error('useStoreClubAuth must be used within StoreClubAuthProvider');
  }
  return context;
};
